'use client';

import { createContext } from 'react';

import {
  PlatformBasicError,
  PlatformFieldError,
  PlatformTableFiltersParameter,
  PlatformTableListRowsTeamParams,
} from '@cloudquery/api-platform';
import { GridPaginationModel, GridSortModel, GridValidRowModel } from '@mui/x-data-grid';

import {
  AssetResourceCategoryMenuItem,
  FilterFieldProps,
  DisplayColumns,
  TableSort,
  TableState,
  AssetInventoryGridColDef,
  DataFilterString,
} from '../../types';

const errorFn = () => {
  throw new Error(
    'TableStateContext is being used without a provider. Make sure to wrap your component tree with TableStateProvider.',
  );
};

interface TableStateContextType {
  apiFilter: { filter: PlatformTableFiltersParameter } | undefined;
  apiSortProps: Pick<PlatformTableListRowsTeamParams, 'sort_by' | 'sort_dir'> | undefined;
  applySavedFilter: (filterId: string, filter: DataFilterString) => void;
  columns: AssetInventoryGridColDef[];
  detailIds?: string[];
  displayColumns: DisplayColumns;
  error: PlatformBasicError | PlatformFieldError | null;
  fetchingResourceCategories: boolean;
  filter: DataFilterString;
  filterFields: FilterFieldProps[];
  hasEditableCustomColumns: boolean;
  isLoading: boolean;
  originalFilter: TableState['originalFilter'];
  overwriteDisplayColumns: (displayColumns: DisplayColumns) => void;
  overwriteFilter: (filter: DataFilterString) => void;
  paginationModel: GridPaginationModel;
  removeSavedFilter: () => void;
  resetDisplayColumns: () => void;
  resetFilter: () => void;
  resetSort: () => void;
  resetTableState: () => void;
  resourceCategories: AssetResourceCategoryMenuItem[];
  resourceCategoriesTotalCount: number;
  rowCount: number;
  rows: GridValidRowModel[];
  savedFilterHasDeviated: boolean;
  setDetailIds: (detailIds?: string[]) => void;
  setDisplayColumns: (toggleColumn: string) => void;
  setPagination: (newModel: GridPaginationModel) => void;
  setSort: (sortModel: GridSortModel) => void;
  sort: TableSort;
  tableName: string;
}

export const defaultContext = {
  apiFilter: undefined,
  apiSortProps: undefined,
  applySavedFilter: errorFn,
  columns: [],
  detailIds: undefined,
  displayColumns: new Set() as DisplayColumns,
  error: null,
  fetchingResourceCategories: false,
  filter: '',
  filterFields: [],
  hasEditableCustomColumns: false,
  isLoading: false,
  originalFilter: undefined,
  overwriteDisplayColumns: errorFn,
  overwriteFilter: errorFn,
  paginationModel: {
    page: 0,
    pageSize: 25,
  },
  removeSavedFilter: errorFn,
  resetDisplayColumns: errorFn,
  resetFilter: errorFn,
  resetSort: errorFn,
  resetTableState: errorFn,
  resourceCategories: [],
  resourceCategoriesTotalCount: 0,
  rowCount: 0,
  rows: [],
  savedFilterHasDeviated: false,
  setDetailIds: errorFn,
  setDisplayColumns: errorFn,
  setPagination: errorFn,
  setSort: errorFn,
  sort: [{ field: 'resource_type', sort: 'asc' }] as TableSort,
  tableName: '',
};

export const TableStateContext = createContext<TableStateContextType>(defaultContext);
